import {Database} from '@tnc/shared';
import clsx from 'clsx';

import {profileDetailsDataItemT} from '@/lib/types/form.types';

import {ProfileDetailsDataItemActionComponent} from '@/components/profileDetailsDataItemActionComponent';
import {ProfileDetailsDataItemGenericComponent} from '@/components/profileDetailsDataItemGenericComponent';
import {ProfileDetailsDataItemVisualComponent} from '@/components/profileDetailsDataItemVisualComponent';

export type profileCardSectionT = {
  title: string;

  items: {
    icon?: string;
    header: string;
    content: string | number | undefined | null;
    avatar?: string;
  }[];
  render?: () => React.ReactNode;
  className?: string;
};

export type profileCardHeaderT = {
  subHeader: string | undefined;
  perks?: {icon: string; content: string}[];
  stats?: {number: number; title: string}[];
  render?: () => React.ReactNode;
};

interface CARD_PROPS {
  avatar?: string;
  name?: string;
  headerInfo?: profileCardHeaderT;
  status?: Database['public']['Enums']['application_status_enum'] | Database['public']['Enums']['retailerstatus'];
  sections?: profileCardSectionT[];
  dataItem?: profileDetailsDataItemT;
  children?: React.ReactNode;
  className?: string;
  headerBg?:
    | 'success'
    | 'info'
    | 'warning'
    | 'danger'
    | 'blueGray-800'
    | 'blueGray-700'
    | 'blueGray-600'
    | 'blueGray-500'
    | 'blueGray-400'
    | 'blueGray-300'
    | 'blueGray-200'
    | 'blueGray-100';
}

/**
 * This component renders the details of a page, meant for informational data.
 * @param {string} avatar - The avatar of the user.
 * @param {string} name - The name of the user.
 * @param {headerT} [headerInfo] - The header information of the user.
 * @param {sectionT[]} [sections] - The sections of the user.
 * @param {profileDetailsDataItemT} [dataItem] - The data item to be rendered.
 * @param {React.ReactNode} [children] - The children of the component.
 * @param {string} [className] - The class name of the component.
 * @param {string} [headerBg] - The background color of the header.
 * @returns {React.ReactNode} - The profile card component.
 */
export const ProfileCardComponent = ({
  avatar,
  name,
  headerInfo,
  sections,
  dataItem,
  children,
  className,
  headerBg,
  status,
}: CARD_PROPS) => {
  return (
    <div className={`${className || 'col-span-12 xl:col-span-12 xxl:col-span-4'}`}>
      <div className={`box overflow-hidden`}>
        <div className="box-body !p-0">
          <div
            className={`main-profile-cover ${
              headerBg ? `before:!important before:bg-${headerBg}` : ''
            } items-start p-6 sm:flex`}
          >
            <div>
              {avatar && (
                <span className="avatar avatar-xxl avatar-rounded online me-4">
                  <img src={avatar} alt={name} className="rounded-md" />
                </span>
              )}
            </div>
            <div className="main-profile-info flex-grow">
              {name && (
                <div className="flex flex-col md:flex-row md:items-stretch md:justify-between">
                  <div>
                    <h1 className="mb-1 grow text-lg font-semibold text-white md:text-xl">{name}</h1>
                    {headerInfo && (
                      <>
                        {headerInfo.render ? (
                          headerInfo.render()
                        ) : (
                          <>
                            <p className="mb-1 !text-white opacity-[0.7]">{headerInfo.subHeader}</p>
                            {headerInfo.perks && (
                              <p className="mb-6 text-[0.75rem] text-white opacity-[0.5]">
                                {headerInfo.perks.map((idx, i) => (
                                  <span key={i} className={i === 0 ? 'me-4 inline-flex' : 'inline-flex'}>
                                    <i className={idx.icon + ' me-1 align-middle'}></i>
                                    <span>{idx.content}</span>
                                  </span>
                                ))}
                              </p>
                            )}

                            {headerInfo.stats && (
                              <div className="mb-0 flex">
                                {headerInfo.stats.map((idx, i) => (
                                  <div key={i} className="me-6">
                                    <p className="text-shadow mb-0 text-[1.25rem] font-bold text-white">{idx.number}</p>
                                    <p className="mb-0 text-[.6875rem] text-white opacity-[0.5]">{idx.title}</p>
                                  </div>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </div>
                  <div className="flex items-stretch">
                    <div className="flex items-center gap-2 text-2xl font-semibold capitalize">
                      <div>
                        <div
                          className={clsx('h-4 w-4 rounded-full', {
                            'bg-success': status === 'approved',
                            'bg-danger': status === 'rejected' || status === 'closed',
                            'bg-warning': status === 'pending' || status === 'in-progress',
                            'bg-info': status === 'draft',
                          })}
                        ></div>
                      </div>
                      <div>{status}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {sections &&
            sections.map((section, index) => (
              <div
                key={index}
                className={clsx('border-b border-dashed p-6 dark:border-defaultborder/10', section.className)}
              >
                <p className="mb-2 text-[.9375rem] font-semibold">{section.title}:</p>
                <div>
                  {section.render
                    ? section.render()
                    : section.items.map((item, i) => (
                        <div key={i} className="mb-2 flex items-center gap-3">
                          {item.avatar && (
                            <span className="avatar avatar-sm mr-2">
                              <img src={item.avatar} alt={item.header} className="rounded-md" />
                            </span>
                          )}
                          <div>
                            <p className="mb-0">{item.header}:</p>
                            <p className="mb-2 text-[#8c9097] dark:text-white/50">{item.content}</p>
                          </div>
                        </div>
                      ))}
                </div>
              </div>
            ))}
        </div>
      </div>

      {
        // render data item based on the type
        dataItem && (
          <>
            {dataItem.type === 'generic' && (
              <ProfileDetailsDataItemGenericComponent
                type="generic"
                title={dataItem.title}
                data={dataItem.data}
                className={dataItem.className || 'col-span-12 xl:col-span-4'}
              />
            )}
            {dataItem.type === 'action' && (
              <ProfileDetailsDataItemActionComponent
                title={dataItem.title}
                data={dataItem.data}
                className={dataItem.className || 'col-span-12 xl:col-span-4'}
                onViewAll={dataItem.onViewAll}
                dataActions={dataItem.dataActions}
              />
            )}
            {dataItem.type === 'visual' && (
              <ProfileDetailsDataItemVisualComponent
                title={dataItem.title}
                data={dataItem.data}
                className={dataItem.className || 'col-span-12 xl:col-span-4'}
                badgeText={dataItem.badgeText}
              />
            )}
          </>
        )
      }

      {children}
    </div>
  );
};
